import React, { useEffect, useState } from 'react';
import {
    Paper, Typography, Box, Chip, IconButton, Button, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Tooltip, CircularProgress, Divider
} from '@mui/material';
import { toast } from 'react-hot-toast';
import { FaMicrochip, FaPlus, FaTrash, FaWifi, FaServer, FaCheckCircle, FaTimesCircle, FaSyncAlt, FaKey } from 'react-icons/fa';
import { api } from '../../services/api';
import { useApp } from '../../context/AppContext';

const MAX_DEVICES = 10;

export default function DeviceManager() {
    const { selectedDeviceId, setSelectedDeviceId, devices, setDevices } = useApp();
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [newId, setNewId] = useState('');
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const res = await api.get('/devices');
            setDevices(res.data.devices || []);
        } catch (e) {
            toast.error('Erro ao carregar dispositivos');
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleAdd = async () => {
        if (!newId.trim()) return toast.error('Informe o ID da placa (ex.: esp32_002)');
        setSaving(true);
        try {
            await api.post('/devices', { deviceId: newId.trim(), name: newName.trim() || newId.trim() });
            toast.success('Dispositivo adicionado!');
            setAddOpen(false);
            setNewId('');
            setNewName('');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Erro ao adicionar');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (id) => {
        if (!window.confirm(`Remover a placa ${id}? Isso apaga também seus comandos.`)) return;
        try {
            await api.delete(`/devices/${id}`);
            toast.success('Dispositivo removido');
            if (selectedDeviceId === id) setSelectedDeviceId('esp32_001');
            await load();
        } catch (e) {
            toast.error('Erro ao remover');
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress size={24} /></Box>;

    return (
        <Paper sx={{ p: 3, mb: 3, background: 'rgba(255,255,255,0.02)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FaServer color="#4CAF50" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Meus Dispositivos</Typography>
                    <Chip size="small" label={`${devices.length}/${MAX_DEVICES}`} variant="outlined" />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Sincronizar lista">
                        <IconButton size="small" onClick={load} disabled={syncing}>
                            {syncing ? <CircularProgress size={16} /> : <FaSyncAlt />}
                        </IconButton>
                    </Tooltip>
                    <Button size="small" startIcon={<FaPlus />} variant="outlined"
                        disabled={devices.length >= MAX_DEVICES}
                        onClick={() => setAddOpen(true)}>
                        Adicionar
                    </Button>
                </Stack>
            </Stack>

            {devices.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Nenhuma placa cadastrada. Conecte a ESP32 ao WiFi (portal AutoGrow_AP) ou use "Adicionar".
                </Typography>
            )}

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {devices.map(d => {
                    const active = d.deviceId === selectedDeviceId;
                    const online = d.status === 'online';
                    return (
                        <Paper key={d.deviceId} onClick={() => setSelectedDeviceId(d.deviceId)}
                            sx={{
                                p: 1.5, minWidth: 150, cursor: 'pointer',
                                border: `2px solid ${active ? '#4CAF50' : 'rgba(255,255,255,0.08)'}`,
                                background: active ? 'rgba(76,175,80,0.12)' : 'rgba(255,255,255,0.03)',
                                transition: 'all .2s',
                                display: 'flex', flexDirection: 'column', gap: 0.5
                            }}>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <FaMicrochip />
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.deviceId}</Typography>
                                </Stack>
                                {online
                                    ? <FaCheckCircle color="#4CAF50" size={14} />
                                    : <FaTimesCircle color="#f44336" size={14} />}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">{d.name}</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
                                <Chip size="small" icon={<FaWifi />} label={online ? 'Online' : 'Offline'}
                                    color={online ? 'success' : 'error'} variant="outlined" />
                                <Tooltip title="Remover placa">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemove(d.deviceId); }}>
                                        <FaTrash size={12} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>

            {selectedDeviceId && (
                <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <FaKey size={12} color="#FFB300" />
                        <Typography variant="caption" color="text.secondary">
                            API Key de <b>{selectedDeviceId}</b> (use na ESP32 / portal AutoGrow_AP):
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: '#FFB300' }}>
                        {devices.find(d => d.deviceId === selectedDeviceId)?.apiKey || '—'}
                    </Typography>
                </Box>
            )}

            <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
                <DialogTitle>Adicionar Dispositivo</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Informe o ID da placa (gerado pela ESP32 ou definido no portal AutoGrow_AP).
                        Ex.: esp32_002, esp32_003...
                    </Typography>
                    <TextField label="Device ID" fullWidth margin="normal" autoFocus
                        value={newId} onChange={e => setNewId(e.target.value)}
                        placeholder="esp32_002" />
                    <TextField label="Nome (opcional)" fullWidth margin="normal"
                        value={newName} onChange={e => setNewName(e.target.value)}
                        placeholder="Estufa 2" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleAdd} disabled={saving}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : 'Adicionar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
